import { Rect, SlotFormat, SlotFrame } from 'editor/src/store/design/types';

import limitPrecision from 'editor/src/util/limitPrecision';

export const MAX_SLOT_ROWS = 6;
export const MAX_SLOT_COLUMNS = 3;

export function isSlotFormat(columns: number): columns is SlotFormat {
  return columns === 1 || columns === 2 || columns === 3;
}

/** The number of products a rows table places. */
export function countSlots(rows: number[]): number {
  return rows.reduce((total, columns) => total + (isSlotFormat(columns) ? columns : 0), 0);
}

/** Product counts whose rows do not place exactly that many products. */
export function getMismatchedCounts(rows: number[][]): number[] {
  return rows.map((countRows, i) => (countSlots(countRows) === i + 1 ? 0 : i + 1)).filter(Boolean);
}

/** The formats any count uses, so the author knows which folders a picture set needs. */
export function getUsedSlotFormats(rows: number[][]): SlotFormat[] {
  const formats = new Set<SlotFormat>();
  rows.forEach((countRows) => countRows.forEach((columns) => isSlotFormat(columns) && formats.add(columns)));
  return [...formats].sort();
}

export function getSlotFrameId(product: number, columns: SlotFormat, row: number, column: number): string {
  return `f-${product}-${columns}-${row}-${column}`;
}

/**
 * The frames every count's rows need, merged across counts. The grid keeps one row height for
 * every count (the tallest table decides the row count), so a product that stays in the same
 * rect from one count to the next keeps its frame and everything the author placed in it.
 */
function computeSlotFrames(rows: number[][], area: Rect, gap: number): SlotFrame[] {
  const rowCount = Math.max(1, ...rows.map((countRows) => countRows.length));
  const rowHeight = (area.height - gap * (rowCount - 1)) / rowCount;
  const frames = new Map<string, SlotFrame>();

  rows.forEach((countRows, countIndex) => {
    const count = countIndex + 1;
    let product = 0;
    countRows.forEach((columns, row) => {
      if (!isSlotFormat(columns)) {
        return;
      }
      const slotWidth = (area.width - gap * (columns - 1)) / columns;
      for (let column = 0; column < columns; column += 1) {
        product += 1;
        const id = getSlotFrameId(product, columns, row, column);
        const frame = frames.get(id);
        if (frame) {
          frame.counts.push(count);
        } else {
          frames.set(id, {
            id,
            product,
            columns,
            x: limitPrecision(area.x + column * (slotWidth + gap)),
            y: limitPrecision(area.y + row * (rowHeight + gap)),
            width: limitPrecision(slotWidth),
            height: limitPrecision(rowHeight),
            counts: [count],
          });
        }
      }
    });
  });

  return [...frames.values()].sort((a, b) => a.product - b.product || a.counts[0] - b.counts[0]);
}

export default computeSlotFrames;
